import {
  AlertTriangle,
  Bot,
  Briefcase,
  Clock,
  DollarSign,
  FileText,
  Inbox,
  MapPin,
  ScrollText,
  Sparkles
} from "lucide-react";
import type { FC, ReactNode } from "react";
import { useNavigate } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { IconTile } from "@/components/ui/icon-tile";
import { ListCard } from "@/components/ui/list-card";
import { SectionHeader } from "@/components/ui/page-header";
import { Tag } from "@/components/ui/tag";
import { cn } from "@/lib/utils";

import { casePath } from "../../caseIde/routes";
import { useTimeStatus } from "../../timeTracking/queries";
import { companyActions } from "../actions";
import { attentionItems, type AttentionItem } from "../companyModel";
import { useCaseRequests, useCompanyBilling } from "../queries";
import { companyPath } from "../routes";
import type { Company, CompanyCaseRow } from "../types";
import { formatAddress } from "../utils";

const Section: FC<{
  title: string;
  aside?: ReactNode;
  children: ReactNode;
}> = ({ title, aside, children }) => (
  <section className="flex flex-col gap-3">
    <SectionHeader title={title} actions={aside} />
    {children}
  </section>
);

const Prop: FC<{ k: string; children: ReactNode }> = ({ k, children }) => (
  <div className="grid min-h-8 grid-cols-[104px_minmax(0,1fr)] items-start gap-3 py-1.5 text-base">
    <span className="text-fg-3">{k}</span>
    <span className="flex min-w-0 flex-wrap gap-1.5 text-fg">{children}</span>
  </div>
);

const Kpi: FC<{
  label: string;
  value: ReactNode;
  sub: string;
  warn?: boolean;
}> = ({ label, value, sub, warn }) => (
  <div className="flex flex-col gap-0.5 px-4 py-3">
    <span className="text-sm text-fg-3">{label}</span>
    <span
      className={cn(
        "text-xl font-medium text-fg tabular-nums",
        warn && "text-warn"
      )}
    >
      {value}
    </span>
    <span className="text-sm text-fg-3">{sub}</span>
  </div>
);

const ICON = {
  proposal: Sparkles,
  playbook: ScrollText,
  deadline: Clock,
  profile: AlertTriangle,
  request: Inbox,
  jobs: Briefcase,
  rates: DollarSign
};
const TONE = {
  red: "text-danger",
  amber: "text-warn",
  primary: "text-primary"
};

type Props = {
  company: Company;
  cases: CompanyCaseRow[];
  /** Opens the profile sheet, optionally on one field. */
  onEdit: (field?: string) => void;
};

const count = (n: number, one: string, many: string) =>
  `${n} ${n === 1 ? one : many}`;

export const OverviewTab: FC<Props> = ({ company, cases, onEdit }) => {
  const navigate = useNavigate();
  const status = useTimeStatus();
  const billing = useCompanyBilling(company.id, status.enabled);
  const requests = useCaseRequests(company.id);
  const items = attentionItems(
    company,
    cases,
    new Date(),
    status.enabled ? billing.data : null,
    requests.data
  );
  const open = cases.filter((c) => c.open && !c.dependent_of);
  const soon = items.filter((i) => i.kind === "deadline").length;
  const hq = company.entities.find((e) => e.is_default) ?? company.entities[0];

  const action = (item: AttentionItem): ReactNode => {
    switch (item.kind) {
      case "proposal":
        return (
          <span className="flex gap-1.5">
            <Button
              variant="secondary"
              size="xs"
              onClick={() =>
                void companyActions.decideProposal(
                  company,
                  item.proposal.id,
                  "accept"
                )
              }
            >
              Save FEIN
            </Button>
            <Button
              size="xs"
              variant="ghost"
              onClick={() =>
                void companyActions.decideProposal(
                  company,
                  item.proposal.id,
                  "dismiss"
                )
              }
            >
              Dismiss
            </Button>
          </span>
        );
      case "deadline":
        return (
          <Button
            size="xs"
            variant="secondary"
            onClick={() =>
              navigate(
                casePath({
                  individualId: item.row.individual_id,
                  foreignNationalId: item.row.foreign_national_id ?? undefined
                })
              )
            }
          >
            Open Case
          </Button>
        );
      case "playbook":
        return (
          <Button
            size="xs"
            variant="secondary"
            onClick={() => navigate(companyPath(company.id, "playbook"))}
          >
            Review
          </Button>
        );
      case "profile":
        return (
          <Button
            size="xs"
            variant="secondary"
            onClick={() => onEdit(item.field)}
          >
            Fix
          </Button>
        );
      case "request":
        return (
          <Button
            variant="secondary"
            size="xs"
            onClick={() =>
              navigate(
                `${companyPath(company.id, "employees")}&request=${
                  item.requestId
                }`
              )
            }
          >
            Review
          </Button>
        );
      case "jobs":
        return (
          <Button
            size="xs"
            variant="secondary"
            onClick={() => navigate(companyPath(company.id, "employees"))}
          >
            Review
          </Button>
        );
      case "rates":
        return (
          <Button
            size="xs"
            variant="secondary"
            onClick={() => navigate(companyPath(company.id, "billing"))}
          >
            Review
          </Button>
        );
      default:
        return null;
    }
  };

  return (
    <div className="flex min-h-0 flex-1" data-testid="company-overview">
      <div className="flex min-h-0 min-w-0 flex-1 flex-col gap-8 overflow-y-auto px-8 py-5">
        <ListCard className="grid shrink-0 grid-cols-2 divide-x divide-y-0 lg:grid-cols-4">
          <Kpi
            label="Open Cases"
            value={open.length}
            sub={`${cases.length - open.length} closed or dependent`}
          />
          <Kpi
            label="Deadlines in 30 Days"
            value={soon}
            sub="Across open cases"
            warn={soon > 0}
          />
          <Kpi
            label="Legal Entities"
            value={company.entities.length}
            sub={count(company.worksites.length, "worksite", "worksites")}
          />
          <Kpi
            label="Contacts"
            value={company.contacts.length}
            sub={count(
              company.job_descriptions.length,
              "job description",
              "job descriptions"
            )}
          />
        </ListCard>

        <Section
          title="Needs Attention"
          aside={
            <span className="text-sm text-fg-4 tabular-nums">
              {items.length}
            </span>
          }
        >
          {items.length ? (
            <ListCard>
              {items.map((item) => {
                const Icon = ICON[item.kind];
                return (
                  <div
                    key={item.id}
                    data-testid={`attention-${item.kind}`}
                    className="flex items-start gap-3 px-4 py-3"
                  >
                    <IconTile
                      icon={Icon}
                      className={cn("mt-0.5", TONE[item.tone])}
                    />
                    <span className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <span className="text-base text-fg">{item.title}</span>
                      <span className="text-sm text-fg-3">{item.sub}</span>
                    </span>
                    {action(item)}
                  </div>
                );
              })}
            </ListCard>
          ) : (
            <p className="text-base text-fg-3">Nothing needs you here.</p>
          )}
        </Section>

        <Section title="Where This Is Used">
          <div className="flex flex-col gap-2.5">
            {[
              {
                icon: FileText,
                t: "Petitioner fields on I-129, I-140, ETA-9035 and ETA-9089 fill from here for every employee case."
              },
              {
                icon: Bot,
                t: `Case agents read the petitioner, job and worksite from the company record, and follow its playbook: ${count(
                  (company.playbook ?? []).filter(
                    (r) => r.status === "active" && r.enabled
                  ).length,
                  "rule",
                  "rules"
                )}, with per-case overrides.`
              },
              {
                icon: Clock,
                t: "Time entries on linked cases carry the company's matter ID, PO and cost center into exports."
              }
            ].map((r) => (
              <span key={r.t} className="flex gap-2.5 text-base text-fg-2">
                <r.icon className="mt-0.5 size-3.5 shrink-0 text-fg-3" />
                {r.t}
              </span>
            ))}
          </div>
        </Section>
      </div>

      <aside className="flex w-80 shrink-0 flex-col overflow-y-auto border-l border-line-2 px-4 py-3">
        <span className="flex h-7 items-center text-sm font-medium text-fg-3">
          Company
        </span>
        <Prop
          k={company.entities.length === 1 ? "Legal Entity" : "Legal Entities"}
        >
          <span className="flex flex-col gap-0.5">
            {company.entities.map((e) => (
              <span key={e.id} className="flex flex-col">
                {e.legal_name}
                <span className="font-mono text-sm text-fg-3">
                  {e.fein ? `FEIN ${e.fein}` : "No FEIN"}
                </span>
              </span>
            ))}
          </span>
        </Prop>
        <Prop k="Signatory">
          {hq?.signatory?.name ? (
            `${hq.signatory.name}${
              hq.signatory.title ? `, ${hq.signatory.title}` : ""
            }`
          ) : (
            <span className="text-fg-4">–</span>
          )}
        </Prop>
        <Prop k="Headquarters">
          {formatAddress(hq?.address) || <span className="text-fg-4">–</span>}
        </Prop>
        {hq?.naics ? <Prop k="NAICS">{hq.naics}</Prop> : null}
        {hq?.us_employees ? (
          <Prop k="US Employees">{hq.us_employees.toLocaleString()}</Prop>
        ) : null}
        <Prop k="Worksites">
          {company.worksites.length ? (
            company.worksites.map((w) => (
              <Tag key={w.id}>
                <MapPin />
                {w.remote
                  ? `${w.name || "Remote"} (remote)`
                  : w.name || w.address.city}
              </Tag>
            ))
          ) : (
            <span className="text-fg-4">–</span>
          )}
        </Prop>
        <Prop k="Job Descriptions">
          {company.job_descriptions.length ? (
            company.job_descriptions.map((j) => (
              <Tag key={j.id} title={j.soc_code}>
                {j.title}
              </Tag>
            ))
          ) : (
            <span className="text-fg-4">–</span>
          )}
        </Prop>
      </aside>
    </div>
  );
};
